import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowRight, ArrowLeft, Cpu, Layers, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';

/* ─────────────────────────────────────────────────────────────── */
/*  Constants                                                       */
/* ─────────────────────────────────────────────────────────────── */
const SPECIAL_FEATURES_OPTIONS = [
  '5-axis', 'deep_bore', 'thin_wall', 'high_speed',
  'multi_spindle', 'live_tooling', 'Swiss_type',
];

const FIELD_META = {
  temperature:         { label: 'Temperature (°C)',   type: 'number', icon: '🌡', hint: 'Operating temperature in Celsius' },
  tolerance:           { label: 'Tolerance (mm)',      type: 'number', icon: '📐', hint: 'Dimensional tolerance in millimetres' },
  topic:               { label: 'Industry Topic',      type: 'text',   icon: '🏭', hint: 'e.g. Aerospace, Automotive, Medical' },
  geometry_complexity: { label: 'Geometry Complexity', type: 'select', icon: '⬡',  hint: 'Complexity of the component geometry', options: ['simple', 'moderate', 'complex'] },
  surface_finish:      { label: 'Surface Finish',      type: 'select', icon: '✦',  hint: 'Required surface finish quality',      options: ['standard', 'medium', 'high'] },
  aerospace_required:  { label: 'Aerospace Certified', type: 'toggle', icon: '🛸', hint: 'Requires aerospace-grade certification' },
};

const DEFAULT_VALUES = {
  temperature: 750, tolerance: 0.01, topic: 'Aerospace',
  geometry_complexity: 'complex', surface_finish: 'high',
  special_features: ['5-axis', 'deep_bore'], aerospace_required: true,
};

/* ─────────────────────────────────────────────────────────────── */
/*  Input base style — lighter glass effect                         */
/* ─────────────────────────────────────────────────────────────── */
const inputBase = (focused) => ({
  background: focused ? 'rgba(216,180,254,0.12)' : 'rgba(255,255,255,0.09)',
  border: `1.5px solid ${focused ? 'rgba(216,180,254,0.7)' : 'rgba(255,255,255,0.18)'}`,
  borderRadius: '12px',
  padding: '11px 14px',
  color: '#f8f4ff',
  fontSize: '14px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  backdropFilter: 'blur(6px)',
  transition: 'all 0.25s',
  boxShadow: focused ? '0 0 0 4px rgba(216,180,254,0.12)' : 'none',
});

/* ─────────────────────────────────────────────────────────────── */
/*  Field components                                                */
/* ─────────────────────────────────────────────────────────────── */
const FieldLabel = ({ icon, label }) => (
  <div className="flex items-center gap-2 mb-2">
    <span className="text-base leading-none">{icon}</span>
    <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#c4b5fd' }}>{label}</span>
  </div>
);

const FieldHint = ({ text }) => (
  <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '5px' }}>{text}</p>
);

const TextField = ({ id, meta, value, onChange }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <FieldLabel icon={meta.icon} label={meta.label} />
      <input id={id} type={meta.type} value={value} step="any"
        onChange={e => onChange(meta.type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={inputBase(focused)} />
      <FieldHint text={meta.hint} />
    </div>
  );
};

const SelectField = ({ id, meta, value, onChange }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <FieldLabel icon={meta.icon} label={meta.label} />
      <select id={id} value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          ...inputBase(focused),
          appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23c084fc' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: '38px',
        }}>
        {meta.options.map(opt => <option key={opt} value={opt} style={{ background: '#2e1065' }}>{opt}</option>)}
      </select>
      <FieldHint text={meta.hint} />
    </div>
  );
};

const ToggleField = ({ meta, value, onChange }) => (
  <div>
    <FieldLabel icon={meta.icon} label={meta.label} />
    <div onClick={() => onChange(!value)} style={{
      display: 'flex', alignItems: 'center', gap: '14px',
      cursor: 'pointer', userSelect: 'none', borderRadius: '12px',
      padding: '11px 14px', transition: 'all 0.25s', backdropFilter: 'blur(6px)',
      background: value ? 'rgba(192,132,252,0.15)' : 'rgba(255,255,255,0.07)',
      border: `1.5px solid ${value ? 'rgba(192,132,252,0.55)' : 'rgba(255,255,255,0.15)'}`,
      boxShadow: value ? '0 0 18px rgba(192,132,252,0.15)' : 'none',
    }}>
      {/* Pill */}
      <div style={{
        position: 'relative', width: '42px', height: '22px', borderRadius: '11px', flexShrink: 0,
        background: value ? 'linear-gradient(135deg, #a855f7, #ec4899)' : 'rgba(255,255,255,0.12)',
        border: `1px solid ${value ? 'rgba(192,132,252,0.5)' : 'rgba(255,255,255,0.15)'}`,
        transition: 'all 0.3s',
        boxShadow: value ? '0 0 10px rgba(168,85,247,0.5)' : 'none',
      }}>
        <div style={{
          position: 'absolute', top: '3px', width: '16px', height: '16px', borderRadius: '50%',
          background: '#fff',
          left: value ? '22px' : '3px',
          transition: 'left 0.3s',
          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }} />
      </div>
      <span style={{
        fontSize: '13px', fontWeight: 600,
        color: value ? '#e9d5ff' : '#94a3b8',
        transition: 'color 0.25s',
      }}>
        {value ? 'YES — Required' : 'NO — Not Required'}
      </span>
    </div>
    <FieldHint text={meta.hint} />
  </div>
);

const SpecialFeaturesField = ({ value, onChange }) => (
  <div>
    <FieldLabel icon="⚙" label="Special Features" />
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '10px',
      background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.15)',
      borderRadius: '14px', padding: '14px', backdropFilter: 'blur(6px)',
    }}>
      {SPECIAL_FEATURES_OPTIONS.map(opt => {
        const active = value.includes(opt);
        return (
          <button key={opt} type="button"
            onClick={() => onChange(active ? value.filter(v => v !== opt) : [...value, opt])}
            style={{
              padding: '7px 18px', borderRadius: '999px', cursor: 'pointer', border: '1.5px solid',
              fontSize: '12px', fontWeight: 600, transition: 'all 0.2s',
              background:     active ? 'linear-gradient(135deg, rgba(168,85,247,0.35), rgba(236,72,153,0.3))' : 'rgba(255,255,255,0.06)',
              borderColor:    active ? 'rgba(192,132,252,0.7)' : 'rgba(255,255,255,0.15)',
              color:          active ? '#f3e8ff' : '#94a3b8',
              boxShadow:      active ? '0 0 14px rgba(168,85,247,0.25)' : 'none',
              transform:      active ? 'translateY(-1px)' : 'none',
            }}>
            {active ? '✓ ' : ''}{opt}
          </button>
        );
      })}
    </div>
    <FieldHint text="Click to toggle machining features" />
  </div>
);

/* ─────────────────────────────────────────────────────────────── */
/*  Result display components                                       */
/* ─────────────────────────────────────────────────────────────── */
const ResultSection = ({ title, icon: Icon, gradientFrom, gradientTo, children }) => (
  <div style={{
    background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)',
    borderRadius: '20px', border: '1.5px solid rgba(255,255,255,0.14)',
    overflow: 'hidden', marginBottom: '20px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '14px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)',
      background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
    }}>
      <Icon size={15} style={{ color: 'rgba(255,255,255,0.9)' }} />
      <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.9)' }}>{title}</span>
    </div>
    <div style={{ padding: '24px' }}>{children}</div>
  </div>
);

const SummaryChip = ({ label, value }) => (
  <div style={{
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: '12px', padding: '12px 14px',
  }}>
    <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#a78bfa', marginBottom: '5px' }}>{label}</div>
    <div style={{ fontSize: '13px', fontWeight: 600, color: '#f1f5f9' }}>{value}</div>
  </div>
);

const StatCard = ({ label, value }) => (
  <div style={{
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px', padding: '12px',
  }}>
    <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '5px', letterSpacing: '0.05em' }}>{label}</div>
    <div style={{
      fontSize: '15px', fontWeight: 700,
      background: 'linear-gradient(135deg, #c084fc, #f472b6)',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    }}>{value}</div>
  </div>
);

const InfoRow = ({ label, value, highlight }) => (
  <div style={{
    display: 'flex', gap: '12px', padding: '11px 0',
    borderBottom: '1px solid rgba(255,255,255,0.07)', flexWrap: 'wrap',
  }}>
    <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a78bfa', minWidth: '110px', paddingTop: '2px', fontWeight: 600 }}>{label}</span>
    <span style={{ fontSize: '13px', color: highlight || '#cbd5e1', flex: 1, lineHeight: 1.5 }}>{value}</span>
  </div>
);

const getMachBadge = (r) => {
  if (!r) return { bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.3)', color: '#94a3b8' };
  const s = r.toLowerCase();
  return s.includes('difficult')
    ? { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.4)', color: '#fca5a5' }
    : s.includes('moderate')
    ? { bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.4)',  color: '#fde68a' }
    : { bg: 'rgba(74,222,128,0.12)',  border: 'rgba(74,222,128,0.4)',  color: '#86efac' };
};

const getRiskStyle = (r) => {
  if (!r) return '#94a3b8';
  const s = r.toLowerCase();
  return s.includes('high') ? '#fca5a5' : s.includes('medium') ? '#fde68a' : '#86efac';
};

/* ─────────────────────────────────────────────────────────────── */
/*  Main page                                                       */
/* ─────────────────────────────────────────────────────────────── */
const AgenticAITool = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [values, setValues]     = useState(DEFAULT_VALUES);
  const [response, setResponse] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => { setIsVisible(true); }, []);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const set = key => val => setValues(v => ({ ...v, [key]: val }));

  const handleSubmit = async () => {
    setLoading(true); setError(null); setResponse(null);
    try {
      const res = await fetch('https://veeramanicadas-rag-be-app.hf.space/analyse', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      setResponse(await res.json());
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden"
      onMouseMove={handleMouseMove}
    >
      {/* Mouse glow */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        background: `radial-gradient(circle 800px at ${mousePosition.x}px ${mousePosition.y}px, rgba(139,92,246,0.35), transparent)`
      }} />

      {/* Floating orbs */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-pulse pointer-events-none" />
      <div className="absolute top-40 right-20 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-pulse pointer-events-none" />
      <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-violet-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-12">

        {/* Back link */}
        <div className={`mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#c4b5fd',
            textDecoration: 'none', fontSize: '14px', transition: 'color 0.2s' }}
            onMouseOver={e => e.currentTarget.style.color = '#f0abfc'}
            onMouseOut={e  => e.currentTarget.style.color = '#c4b5fd'}>
            <ArrowLeft size={16} /> Back to Projects
          </Link>
        </div>

        {/* Heading */}
        <div className={`text-center mb-12 transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl blur-xl opacity-60 animate-pulse" />
              <div className="relative bg-gradient-to-r from-purple-500 to-pink-500 p-4 rounded-2xl shadow-xl">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
            </div>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-white mb-3 leading-tight">
            <span className="block bg-gradient-to-r from-violet-300 via-pink-300 to-purple-300 bg-clip-text text-transparent" style={{ paddingBottom: '0.35em' }} >
              Agentic AI Tool
            </span>
            <span className="block text-2xl md:text-3xl mt-2" style={{ color: '#e2d9f3', fontWeight: 400 }}>
            Identify the suitable precision engineering Machine & Material advices for your components!
            </span>
          </h1>
          <p style={{ color: '#c4b5fd', fontSize: '16px', maxWidth: '480px', margin: '0 auto' }}>
            Currently, the analysis and report are based on limited data. More data will be added shortly. 
          </p>
        </div>

        {/* Form card */}
        <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div style={{
            background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(16px)',
            borderRadius: '24px', border: '1.5px solid rgba(255,255,255,0.18)',
            padding: '36px', marginBottom: '24px',
            boxShadow: '0 8px 40px rgba(139,92,246,0.15), 0 2px 10px rgba(0,0,0,0.2)',
            transition: 'box-shadow 0.3s',
          }}>
            {/* Section header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
              <div style={{
                padding: '8px', borderRadius: '12px',
                background: 'linear-gradient(135deg, #7c3aed, #db2777)',
                boxShadow: '0 4px 12px rgba(124,58,237,0.4)',
              }}>
                <Cpu size={16} color="#fff" />
              </div>
              <span style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ddd6fe' }}>
                Component Parameters
              </span>
            </div>

            {/* Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: '24px', marginBottom: '24px' }}>
              {Object.entries(FIELD_META).map(([key, meta]) => {
                if (meta.type === 'number' || meta.type === 'text')
                  return <TextField key={key} id={key} meta={meta} value={values[key]} onChange={set(key)} />;
                if (meta.type === 'select')
                  return <SelectField key={key} id={key} meta={meta} value={values[key]} onChange={set(key)} />;
                if (meta.type === 'toggle')
                  return <ToggleField key={key} meta={meta} value={values[key]} onChange={set(key)} />;
                return null;
              })}
            </div>

            <SpecialFeaturesField value={values.special_features} onChange={set('special_features')} />

            {/* JSON preview */}
            <details style={{ marginTop: '22px' }}>
              <summary style={{
                fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase',
                color: '#7c6fa0', cursor: 'pointer', userSelect: 'none',
                display: 'flex', alignItems: 'center', gap: '8px', listStyle: 'none',
              }}>
                <span style={{ color: '#a78bfa', fontSize: '10px' }}>▶</span>
                Preview JSON Payload
              </summary>
              <pre style={{
                marginTop: '12px', padding: '16px', borderRadius: '12px',
                background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)',
                color: '#c4b5fd', fontSize: '12px', lineHeight: '1.7',
                overflowX: 'auto', fontFamily: '"Fira Code", monospace',
              }}>{JSON.stringify(values, null, 2)}</pre>
            </details>

            {/* Submit */}
            <button
              onClick={handleSubmit} disabled={loading}
              className="mt-8 w-full group relative font-semibold rounded-full overflow-hidden transition-all duration-300"
              style={{
                padding: '15px 32px',
                background: loading
                  ? 'rgba(255,255,255,0.06)'
                  : 'linear-gradient(135deg, #7c3aed, #db2777)',
                border: loading ? '1.5px solid rgba(255,255,255,0.1)' : 'none',
                color: loading ? '#64748b' : '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px', letterSpacing: '0.05em',
                boxShadow: loading ? 'none' : '0 4px 24px rgba(124,58,237,0.45), 0 2px 8px rgba(219,39,119,0.3)',
                transform: loading ? 'none' : undefined,
                transition: 'all 0.3s',
              }}
              onMouseOver={e => { if (!loading) e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseOut={e  => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <span style={{
                    display: 'inline-block', width: '16px', height: '16px',
                    border: '2px solid rgba(255,255,255,0.2)', borderTopColor: '#c084fc',
                    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                  }} />
                  Analyzing Parameters…
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  Run Agentic Analysis
                  <ArrowRight size={18} style={{ transition: 'transform 0.2s' }} />
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: 'rgba(248,113,113,0.12)', border: '1.5px solid rgba(248,113,113,0.35)',
            borderRadius: '16px', padding: '16px 20px', marginBottom: '24px',
            color: '#fca5a5', fontSize: '14px',
          }}>⚠ {error}</div>
        )}

        {/* Results */}
        {response && (
          <div style={{ animation: 'fadeUp 0.55s ease forwards' }}>

            {/* Status */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              background: 'rgba(74,222,128,0.1)', border: '1.5px solid rgba(74,222,128,0.3)',
              borderRadius: '16px', padding: '13px 20px', marginBottom: '24px',
            }}>
              <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 10px rgba(74,222,128,0.8)' }} />
              <span style={{ color: '#86efac', fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                Analysis Complete · Status: {response.status}
              </span>
            </div>

            {/* Request Summary */}
            {response.request_summary && (
              <ResultSection title="Request Summary" icon={Layers} gradientFrom="rgba(124,58,237,0.5)" gradientTo="rgba(219,39,119,0.4)">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: '12px' }}>
                  {Object.entries(response.request_summary).map(([k, v]) => (
                    <SummaryChip key={k} label={k.replace(/_/g, ' ')} value={Array.isArray(v) ? v.join(', ') : String(v)} />
                  ))}
                </div>
              </ResultSection>
            )}

            {/* Material Analysis */}
            {response.material_analysis && (
              <ResultSection title="Material Analysis" icon={Sparkles} gradientFrom="rgba(109,40,217,0.55)" gradientTo="rgba(147,51,234,0.4)">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                  {[
                    { lbl: 'Primary Material', val: response.material_analysis.primary_material_name, from: '#7c3aed', to: '#9333ea' },
                    { lbl: 'Type',              val: response.material_analysis.primary_material_type, from: '#4f46e5', to: '#7c3aed' },
                  ].map(({ lbl, val, from, to }) => (
                    <div key={lbl} style={{
                      padding: '8px 18px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
                      background: `linear-gradient(135deg, ${from}, ${to})`,
                      color: '#fff', boxShadow: `0 2px 12px ${from}55`,
                    }}>
                      <span style={{ opacity: 0.7, marginRight: '6px' }}>{lbl}:</span>{val || '—'}
                    </div>
                  ))}
                  {response.material_analysis.primary_machinability_rating && (() => {
                    const b = getMachBadge(response.material_analysis.primary_machinability_rating);
                    return (
                      <div style={{ padding: '8px 18px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
                        background: b.bg, border: `1.5px solid ${b.border}`, color: b.color }}>
                        Machinability: {response.material_analysis.primary_machinability_rating}
                      </div>
                    );
                  })()}
                </div>

                {response.material_analysis.comparison_summary && (
                  <p style={{
                    color: '#c4b5fd', fontSize: '14px', fontStyle: 'italic', lineHeight: 1.7,
                    margin: '0 0 20px', paddingLeft: '16px',
                    borderLeft: '3px solid rgba(192,132,252,0.5)',
                  }}>"{response.material_analysis.comparison_summary}"</p>
                )}

                {response.material_analysis.recommended_materials?.map((mat, i) => {
                  const b = getMachBadge(mat.machinability_rating);
                  return (
                    <div key={i} style={{
                      background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.13)',
                      borderRadius: '16px', padding: '20px', marginTop: '16px',
                      boxShadow: '0 2px 16px rgba(0,0,0,0.1)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
                        <div>
                          <div style={{ fontSize: '18px', fontWeight: 700, color: '#f0e6ff' }}>{mat.material_name}</div>
                          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '3px' }}>{mat.material_type} · {mat.grade}</div>
                        </div>
                        <span style={{ padding: '5px 14px', borderRadius: '999px', fontSize: '12px', fontWeight: 700,
                          background: b.bg, border: `1.5px solid ${b.border}`, color: b.color, alignSelf: 'flex-start' }}>
                          {mat.machinability_rating}
                        </span>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                        {[['Tensile Strength', `${mat.tensile_strength_mpa} MPa`], ['Yield Strength', `${mat.yield_strength_mpa} MPa`],
                          ['Max Temp', `${mat.max_operating_temp_c}°C`], ['Cost / kg', `$${mat.cost_per_kg}`]
                        ].map(([lbl, val]) => <StatCard key={lbl} label={lbl} value={val} />)}
                      </div>

                      {mat.reason     && <p style={{ fontSize: '13px', color: '#94a3b8',  marginBottom: '6px' }}><span style={{ color: '#7c6fa0', marginRight: '8px' }}>Reason:</span>{mat.reason}</p>}
                      {mat.advantages && <p style={{ fontSize: '13px', color: '#86efac',  marginBottom: '4px' }}><span style={{ color: '#7c6fa0', marginRight: '8px' }}>Advantages:</span>{mat.advantages}</p>}
                      {mat.tradeoffs  && <p style={{ fontSize: '13px', color: '#fca5a5' }}><span style={{ color: '#7c6fa0', marginRight: '8px' }}>Tradeoffs:</span>{mat.tradeoffs}</p>}
                    </div>
                  );
                })}
              </ResultSection>
            )}

            {/* Machine Analysis */}
            {response.machine_analysis && (
              <ResultSection title="Machine Analysis" icon={Wrench} gradientFrom="rgba(219,39,119,0.5)" gradientTo="rgba(244,63,94,0.4)">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                  <div style={{
                    padding: '8px 18px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
                    background: 'linear-gradient(135deg, #be185d, #e11d48)',
                    color: '#fff', boxShadow: '0 2px 12px rgba(190,24,93,0.4)',
                  }}>
                    <span style={{ opacity: 0.7, marginRight: '6px' }}>Material:</span>
                    {response.machine_analysis.selected_material_name || '—'}
                  </div>
                  {response.machine_analysis.manufacturing_risk && (() => {
                    const isHigh = response.machine_analysis.manufacturing_risk.toLowerCase().includes('high');
                    return (
                      <div style={{
                        padding: '8px 18px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
                        background: isHigh ? 'rgba(248,113,113,0.14)' : 'rgba(251,191,36,0.14)',
                        border: `1.5px solid ${isHigh ? 'rgba(248,113,113,0.45)' : 'rgba(251,191,36,0.45)'}`,
                        color: isHigh ? '#fca5a5' : '#fde68a',
                      }}>
                        Risk: {response.machine_analysis.manufacturing_risk.split(' ')[0]}
                      </div>
                    );
                  })()}
                  {response.machine_analysis.estimated_machining_time_category && (
                    <div style={{
                      padding: '8px 18px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
                      background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#cbd5e1',
                    }}>
                      Est. Time: {response.machine_analysis.estimated_machining_time_category}
                    </div>
                  )}
                </div>

                {response.machine_analysis.machining_strategy && <InfoRow label="Strategy"   value={response.machine_analysis.machining_strategy} />}
                {response.machine_analysis.manufacturing_risk  && <InfoRow label="Risk"        value={response.machine_analysis.manufacturing_risk} highlight={getRiskStyle(response.machine_analysis.manufacturing_risk)} />}
                {response.machine_analysis.risk_mitigations    && <InfoRow label="Mitigations" value={response.machine_analysis.risk_mitigations} />}

                {/* ── Recommended Machine Cards ── */}
                {response.machine_analysis.recommended_machines?.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
                      color: '#f9a8d4', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ display: 'inline-block', width: '18px', height: '1.5px', background: 'rgba(249,168,212,0.5)' }} />
                      Recommended Machines ({response.machine_analysis.recommended_machines.length})
                    </div>
                    {response.machine_analysis.recommended_machines.map((mach, i) => (
                      <div key={i} style={{
                        background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(244,114,182,0.2)',
                        borderRadius: '16px', padding: '20px', marginBottom: '12px',
                        boxShadow: '0 2px 16px rgba(219,39,119,0.08)',
                      }}>
                        {/* Machine header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
                          <div>
                            <div style={{ fontSize: '17px', fontWeight: 700, color: '#fce7f3' }}>{mach.machine_name}</div>
                            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '3px' }}>
                              {mach.machine_type} · Compatible: {mach.material_compatibility}
                            </div>
                          </div>
                          <div style={{
                            padding: '5px 16px', borderRadius: '999px', fontSize: '12px', fontWeight: 700, alignSelf: 'flex-start',
                            background: 'linear-gradient(135deg, rgba(219,39,119,0.25), rgba(244,63,94,0.2))',
                            border: '1.5px solid rgba(244,114,182,0.45)', color: '#fda4af',
                          }}>
                            {mach.machine_type}
                          </div>
                        </div>

                        {/* Stats grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                          {[
                            ['Tolerance', mach.tolerance_capability],
                            ['Cost / hr',  `$${mach.cost_per_hour}`],
                            ['Material',   mach.material_compatibility],
                            ['Operations', mach.operations?.join(', ')],
                          ].map(([lbl, val]) => val && (
                            <div key={lbl} style={{
                              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: '12px', padding: '10px 12px',
                            }}>
                              <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '4px', letterSpacing: '0.05em' }}>{lbl}</div>
                              <div style={{
                                fontSize: '14px', fontWeight: 700,
                                background: 'linear-gradient(135deg, #f9a8d4, #fda4af)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                              }}>{val}</div>
                            </div>
                          ))}
                        </div>

                        {/* Reason */}
                        {mach.reason && (
                          <p style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.6, margin: 0 }}>
                            <span style={{ color: '#7c6fa0', marginRight: '8px' }}>Reason:</span>{mach.reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Planning summary */}
                {response.machine_analysis.planning_summary && (
                  <p style={{ color: '#f9a8d4', fontSize: '14px', fontStyle: 'italic', lineHeight: 1.7,
                    marginTop: '20px', paddingLeft: '16px', borderLeft: '3px solid rgba(244,114,182,0.5)' }}>
                    "{response.machine_analysis.planning_summary}"
                  </p>
                )}

                {response.machine_analysis.recommended_machines?.length === 0 && (
                  <div style={{ marginTop: '16px', background: 'rgba(248,113,113,0.1)', border: '1.5px solid rgba(248,113,113,0.3)',
                    borderRadius: '12px', padding: '16px', color: '#fca5a5', fontSize: '14px', lineHeight: 1.6 }}>
                    No suitable machines found for the selected material and requirements.
                    Consider alternative materials or manufacturing processes.
                  </div>
                )}
              </ResultSection>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes fadeUp  { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        select option { background: #2e1065; color: #f8f4ff; }
      `}</style>
    </div>
  );
};

export default AgenticAITool;